import React, { useState, useEffect } from "react";
import { MdDelete, MdEdit, MdAdd, MdFileDownload, MdSort } from "react-icons/md";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Spinner as SpinnerBoot } from "react-bootstrap";
import { useSelector } from "react-redux";
import { SendPostRequest } from "../../http";
import Spinner from "../Spinner";
import { formatDate, today } from "../../Helper/Helper";
import Select from "react-select";

// Sorting options
const sortOptions = [
  { value: "date_desc", label: "Sort by Date (Newest)" },
  { value: "date_asc", label: "Sort by Date (Oldest)" },
  { value: "weight_desc", label: "Sort by Weight (High → Low)" },
  { value: "weight_asc", label: "Sort by Weight (Low → High)" },
];

// Custom CSS for active tab styling
const activeTabStyles = `
  .btn-group .btn.active-view {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  .btn-group .btn.active-unit {
    background-color: #000 !important;
    border-color: #000 !important;
    color: white !important;
  }
  
  .btn-primary:not(:disabled):not(.disabled):active,
  .btn-primary:not(:disabled):not(.disabled).active,
  .show > .btn-primary.dropdown-toggle {
    background-color: #007bff !important;
    border-color: #007bff !important;
  }
  
  /* Action column styling */
  .action-column {
    width: 120px !important;
    padding-right: 5px !important;
  }
  
  .action-buttons {
    display: flex;
    justify-content: flex-end;
    padding-right: 5px;
  }
  
  .action-buttons .btn {
    padding: 0.25rem 0.4rem;
  }
  
  /* Add Record button styling */
  .btn-add-record {
    background-color: #dc3545 !important;
    border-color: #dc3545 !important;
  }
  
  .btn-add-record:hover {
    background-color: #bb2d3b !important;
    border-color: #bb2d3b !important;
  }
`;

function WeightManagementList() {
  const patientData = useSelector((store) => store.patient.patientLogin);
  const [isLoading, setIsLoading] = useState(false);
  const [LoadingEdit, setLoadingEdit] = useState(false);
  const [LoadingDelete, setLoadingDelete] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filterData, setFilterData] = useState({ date_from: "", date_to: "" });
  const [deleteId, setDeleteId] = useState(null);
  const [getData, setGetData] = useState([]);
  const [editData, setEditData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortBy, setSortBy] = useState("date_desc");
  const [addData, setAddData] = useState({
    weight_value: "",
    record_date: "",
    notes: "",
    unit: "kg"
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [weightUnit, setWeightUnit] = useState("kg"); // 'kg' or 'lbs'
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [currentFilter, setCurrentFilter] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = activeTabStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    // When unit changes, refetch data with current filter if active
    if (isFilterActive && currentFilter) {
      fetchData(currentFilter);
    } else {
      fetchData();
    }
  }, [weightUnit]);

  const fetchData = async (filter = null) => {
    setIsLoading(true);
    try {
      // Store the current filter
      if (filter) {
        setCurrentFilter(filter);
      }

      const response = await SendPostRequest("healthcare/weight/grid", {
        logged_id: patientData?.logged_id,
        filter: filter || {},
        unit: weightUnit // Send the current unit preference
      });

      if (response?.status === "success") {
        // Format data based on selected unit
        const formattedData = response?.data?.map(item => {
          // Use the appropriate field based on selected unit
          const weightValue = weightUnit === 'kg'
            ? parseFloat(item.in_kg)
            : parseFloat(item.in_lbs);

          return {
            ...item,
            weight_value: weightValue
          };
        }) || [];

        // Sort data based on current sort option
        const sortedData = sortData(formattedData, sortBy);
        setGetData(sortedData);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const sortData = (data, sortOption) => {
    if (!data) return [];
    let sortedData = [...data];
    if (sortOption === "date_asc") {
      sortedData.sort((a, b) => new Date(a.record_date) - new Date(b.record_date));
    } else if (sortOption === "date_desc") {
      sortedData.sort((a, b) => new Date(b.record_date) - new Date(a.record_date));
    } else if (sortOption === "weight_asc") {
      sortedData.sort((a, b) => a.weight_value - b.weight_value);
    } else if (sortOption === "weight_desc") {
      sortedData.sort((a, b) => b.weight_value - a.weight_value);
    }
    return sortedData;
  };

  const handleSortChange = (selectedOption) => {
    const value = selectedOption.value;
    setSortBy(value);
    setGetData(sortData(getData, value));
  };

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData({});
    setValidationErrors({});
    setAddData({
      weight_value: "",
      record_date: "",
      notes: "",
      unit: "kg"
    });
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    // Validate inputs
    let errors = { ...validationErrors };

    if (name === 'weight_value') {
      // Check for more than one decimal place
      if (value.includes('.') && (value.split('.')[1] || '').length > 1) {
        errors[name] = 'You can only enter up to one decimal place';
      }
      // Prevent negative values and allow only numbers with up to one decimal place
      else if (!/^\d*\.?\d*$/.test(value) || value.includes('-')) {
        errors[name] = 'Please enter a valid positive number';
      } else {
        const numValue = parseFloat(value);
        const currentUnit = editData.id ? editData.unit : addData.unit;

        if (currentUnit === 'kg') {
          if (numValue < 2) {
            errors[name] = 'Weight must be at least 2 kg';
          } else if (numValue > 700) {
            errors[name] = 'Weight must be less than 700 kg';
          } else {
            delete errors[name];
          }
        } else {
          if (numValue < 4) {
            errors[name] = 'Weight must be at least 4 lbs';
          } else if (numValue > 1550) {
            errors[name] = 'Weight must be less than 1550 lbs';
          } else {
            delete errors[name];
          }
        }
      }
    }

    setValidationErrors(errors);

    if (editData.id) {
      setEditData((prv) => ({ ...prv, [name]: value }));
    } else {
      setAddData((prv) => ({ ...prv, [name]: value }));
    }
  };

  const handleUnitChange = (e) => {
    const { value } = e.target;
    setAddData(prev => ({ ...prev, unit: value }));

    // Clear validation errors when unit changes
    setValidationErrors({});
  };

  const validateForm = (formData) => {
    const errors = {};
    const weightValue = parseFloat(formData.weight_value);
    const currentUnit = formData.unit;

    if (!formData.weight_value || isNaN(weightValue)) {
      errors.weight_value = 'Please enter a valid weight';
    } else if (currentUnit === 'kg') {
      if (weightValue < 2) errors.weight_value = 'Weight must be at least 2 kg';
      if (weightValue > 700) errors.weight_value = 'Weight must be less than 700 kg';
    } else {
      if (weightValue < 4) errors.weight_value = 'Weight must be at least 4 lbs';
      if (weightValue > 1550) errors.weight_value = 'Weight must be less than 1550 lbs';
    }

    if (!formData.record_date) {
      errors.record_date = 'Date is required';
    }

    // Check if date is in the future
    if (formData.record_date && new Date(formData.record_date) > new Date()) {
      errors.record_date = 'Future dates are not allowed';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!validateForm(editData)) {
      return;
    }

    // Convert weight to kg for storage if needed
    let weightValueToStore = parseFloat(editData.weight_value);
    if (editData.unit === 'lbs') {
      weightValueToStore = weightValueToStore / 2.20462; // Convert lbs to kg
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      record_id: editData?.id,
      form_data: {
        in_kg: weightValueToStore.toFixed(1),
        in_lbs: (weightValueToStore * 2.20462).toFixed(1),
        record_date: editData?.record_date,
        notes: editData?.notes,
      },
    };

    setLoadingEdit(true);
    try {
      const response = await SendPostRequest(
        "healthcare/weight/edit",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        handleModalClose();
        // Refetch data with current filter if active
        if (isFilterActive && currentFilter) {
          fetchData(currentFilter);
        } else {
          fetchData();
        }
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to update record");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateForm(addData)) {
      return;
    }

    // Convert weight to kg for storage if needed
    let weightValueToStore = parseFloat(addData.weight_value);
    if (addData.unit === 'lbs') {
      weightValueToStore = weightValueToStore / 2.20462; // Convert lbs to kg
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      form_data: {
        in_kg: weightValueToStore.toFixed(1),
        in_lbs: (weightValueToStore * 2.20462).toFixed(1),
        record_date: addData.record_date,
        notes: addData.notes,
      },
    };

    setIsLoading(true);
    try {
      const response = await SendPostRequest(
        "healthcare/weight/add",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success("Record has been added successfully");
        handleModalClose();
        setAddData({
          weight_value: "",
          record_date: "",
          notes: "",
          unit: "kg"
        });
        // Refetch data with current filter if active
        if (isFilterActive && currentFilter) {
          fetchData(currentFilter);
        } else {
          fetchData();
        }
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to add record");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const data = {
      logged_id: patientData?.logged_id,
      record_id: deleteId,
    };

    setLoadingDelete(true);
    try {
      const response = await SendPostRequest(
        "healthcare/weight/delete",
        data
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        // Refetch data with current filter if active
        if (isFilterActive && currentFilter) {
          fetchData(currentFilter);
        } else {
          fetchData();
        }
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete record");
    } finally {
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleFilterChange = (e) => {
    const { value, name } = e.target;
    setFilterData((prv) => ({ ...prv, [name]: value }));
  };

  const handleFilter = async (e) => {
    e.preventDefault();
    if (!filterData?.date_from || !filterData?.date_to) {
      toast.error("Please select both from and to dates");
      return;
    }

    if (new Date(filterData.date_from) > new Date(filterData.date_to)) {
      toast.error("From date cannot be after to date");
      return;
    }

    setIsFilterActive(true);
    setCurrentFilter(filterData);
    fetchData(filterData);
  };

  const clearFilter = () => {
    setFilterData({ date_from: "", date_to: "" });
    setIsFilterActive(false);
    setCurrentFilter(null);
    fetchData({});
  };

  const exportToCSV = () => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      // Sort data by date descending for export
      const sortedData = [...getData].sort((a, b) =>
        new Date(b.record_date) - new Date(a.record_date)
      );

      const headers = ["SR.No", "Record Date", `Weight (${weightUnit})`, "Note", "Status"];
      const csvData = sortedData.map((item, index) => [
        index + 1,
        formatDate(item.record_date),
        item.weight_value,
        item.notes || "--",
        item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "--"
      ]);

      // Add summary statistics
      if (sortedData.length > 0) {
        const weights = sortedData.map(item => item.weight_value);
        const minWeight = Math.min(...weights);
        const maxWeight = Math.max(...weights);
        const avgWeight = (weights.reduce((a, b) => a + b, 0) / weights.length).toFixed(1);

        csvData.push([]);
        csvData.push(["Summary Statistics"]);
        csvData.push(["Minimum Weight", `${minWeight} ${weightUnit}`]);
        csvData.push(["Maximum Weight", `${maxWeight} ${weightUnit}`]);
        csvData.push(["Average Weight", `${avgWeight} ${weightUnit}`]);
        csvData.push([]);
        csvData.push(["Generated On", formatDate(new Date())]);
      }

      const csvContent = [
        headers.join(","),
        ...csvData.map(row => row.map(field => `"${field}"`).join(","))
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `weight_management_${today}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = () => {
    if (isExporting) return;
    setIsExporting(true);

    // Import jsPDF dynamically to avoid SSR issues
    import('jspdf').then(jsPDFModule => {
      import('jspdf-autotable').then(autoTableModule => {
        try {
          const { jsPDF } = jsPDFModule;
          const { default: autoTable } = autoTableModule;

          const doc = new jsPDF();

          // Title
          doc.setFontSize(18);
          doc.text("Weight Management Records", 14, 22);
          doc.setFontSize(11);
          doc.setTextColor(100);
          doc.text(`Generated On: ${formatDate(new Date())}`, 14, 30)

          // Date range if filtered
          if (filterData?.date_from && filterData?.date_to) {
            doc.setFontSize(11);
            doc.text(`Date Range: ${formatDate(filterData.date_from)} to ${formatDate(filterData.date_to)}`, 14, 30);
          }

          // Sort data by date descending for PDF
          const sortedData = [...getData].sort((a, b) =>
            new Date(b.record_date) - new Date(a.record_date)
          );

          // Table data
          const tableData = sortedData.map((item, index) => [
            index + 1,
            formatDate(item.record_date),
            item.weight_value,
            item.notes || "--",
            item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : "--"
          ]);

          // Table
          autoTable(doc, {
            startY: 35,
            head: [['SR.No', 'Date', `Weight (${weightUnit})`, 'Notes', 'Status']],
            body: tableData,
            theme: 'grid',
            headStyles: {
              fillColor: [66, 139, 202]
            }
          });

          // Add summary statistics
          if (sortedData.length > 0) {
            const weights = sortedData.map(item => item.weight_value);
            const minWeight = Math.min(...weights);
            const maxWeight = Math.max(...weights);
            const avgWeight = (weights.reduce((a, b) => a + b, 0) / weights.length).toFixed(1);

            let finalY = doc.lastAutoTable.finalY + 10;

            doc.setFontSize(12);
            doc.text("Summary Statistics", 14, finalY);

            doc.setFontSize(10);
            doc.text(`Minimum Weight: ${minWeight} ${weightUnit}`, 14, finalY + 7);
            doc.text(`Maximum Weight: ${maxWeight} ${weightUnit}`, 14, finalY + 14);
            doc.text(`Average Weight: ${avgWeight} ${weightUnit}`, 14, finalY + 21);

            // doc.text(`Report Generated On: ${formatDate(new Date())}`, 14, finalY + 35);
          }

          doc.save(`weight_records_${today}.pdf`);
        } catch (error) {
          console.error("Error generating PDF:", error);
          toast.error("Failed to generate PDF");
        } finally {
          setIsExporting(false);
        }
      });
    }).catch(error => {
      console.error("Error loading PDF libraries:", error);
      toast.error("Failed to generate PDF");
      setIsExporting(false);
    });
  };

  const getStatusClass = (status) => {
    // Map API status to Bootstrap color classes
    switch (status) {
      case "underweight": return "warning";
      case "normal": return "success";
      case "overweight": return "warning";
      case "obese": return "danger";
      default: return "secondary";
    }
  };

  // Prevent negative values from being entered
  const handleKeyPress = (e) => {
    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
      e.preventDefault();
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="Prescripition">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h3>Weight Management</h3>
        <div
          className="d-flex gap-2 flex-wrap"
          style={{
            justifyContent: isMobile ? "space-between" : "flex-end",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <button
            className="btn btn-default d-flex"
            style={{ alignItems: "center", minWidth: 140 }}
            onClick={handleModalOpen}
          >
            <MdAdd className="me-1" /> Add Record
          </button>
          <div>
            <button
              className="btn btn-default"
              style={{ alignItems: "center" }}
              onClick={exportToCSV}
              disabled={getData.length === 0 || isExporting}
            >
              <MdFileDownload className="me-1" /> CSV
            </button>
            <button
              className="btn btn-default"
              style={{ alignItems: "center" }}
              onClick={exportToPDF}
              disabled={getData.length === 0 || isExporting}
            >
              <MdFileDownload className="me-1" /> PDF
            </button>
          </div>
        </div>
      </div>

      {/* Filter + unit toggle row */}
      <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
        <form
          className="d-flex flex-wrap align-items-end gap-3"
          onSubmit={handleFilter}
        >
          <div className="form-group mb-0">
            <label className="form-label mb-0">From:</label>
            <input
              type="date"
              required
              name="date_from"
              className="form-control"
              value={filterData?.date_from || ""}
              onChange={handleFilterChange}
              max={today}
              style={{ width: "auto", minWidth: 150 }}
            />
          </div>
          <div className="form-group mb-0">
            <label className="form-label mb-0">To:</label>
            <input
              type="date"
              required
              name="date_to"
              className="form-control"
              value={filterData?.date_to || ""}
              onChange={handleFilterChange}
              max={today}
              style={{ width: "auto", minWidth: 150 }}
            />
          </div>
          <button type="submit" className="btn btn-default">
            Filter
          </button>
          <button
            type="button"
            className="btn btn-default"
            onClick={clearFilter}
          >
            Clear
          </button>
        </form>
        <div className="d-flex align-items-end ms-auto mt-2 mt-md-0">
          <div className="d-flex align-items-center ms-2">
            <MdSort className="me-1" />
            <Select
              options={sortOptions}
              value={sortOptions.find((option) => option.value === sortBy)}
              onChange={handleSortChange}
              className="react-select-container weight-modal-select"
              classNamePrefix="react-select"
              isSearchable={false}
              styles={{
                control: (provided) => ({
                  ...provided,
                  minWidth: "180px",
                }),
              }}
            />
          </div>
          <div className="d-flex align-items-center ms-2" role="group">
            <button
              type="button"
              className={`chart-views-btn ${
                weightUnit === "kg"
                  ? "chart-active-views-btn"
                  : "btn-outline-primary"
              }`}
              onClick={() => setWeightUnit("kg")}
            >
              kg
            </button>
            <button
              type="button"
              className={`chart-views-btn-second ${
                weightUnit === "lbs"
                  ? "chart-active-views-btn"
                  : "chart-views-btn-second"
              }`}
              onClick={() => setWeightUnit("lbs")}
            >
              lbs
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-4">
          <Spinner />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table mt-2">
            <thead className="thead-light">
              <tr>
                <th scope="col" style={{ width: "10%", textAlign: "center" }}>
                  SR.No
                </th>
                <th scope="col">Record Date</th>
                <th scope="col">Weight ({weightUnit})</th>
                {/* <th scope="col">Note</th> */}
                <th scope="col">Status</th>
                <th scope="col" className="action-column">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {getData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    No weight records found
                  </td>
                </tr>
              ) : (
                getData.map((item, index) => {
                  const statusColor = item?.color;

                  return (
                    <tr key={item.id || index}>
                      <td style={{ textAlign: "center" }}>{index + 1}</td>
                      <td>{formatDate(item.record_date)}</td>
                      <td>{item.weight_value}</td>
                      {/* <td>{item.notes || "-"}</td> */}
                      <td className="col-2">
                        <span
                          className={`badge`}
                          style={{
                            backgroundColor: statusColor,
                            color: "#fff",
                            textTransform: "capitalize",
                            fontSize: "10px",
                          }}
                        >
                          {item.status || "--"}
                        </span>
                      </td>
                      <td className="action-column">
                        <div className="action-buttons">
                          <button
                            className="btn btn-default me-1 btn-sm"
                            onClick={() => {
                              setEditData({
                                ...item,
                                unit: weightUnit,
                                weight_value: item.weight_value,
                              });
                              handleModalOpen();
                            }}
                            title="Edit"
                          >
                            <MdEdit />
                          </button>
                          <button
                            className="btn btn-default"
                            onClick={() => {
                              setDeleteId(item.id);
                              setShowDeleteConfirm(true);
                            }}
                            disabled={LoadingDelete}
                            title="Delete"
                          >
                            {LoadingDelete && deleteId === item.id ? (
                              <SpinnerBoot size="sm" />
                            ) : (
                              <MdDelete />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editData.id ? "Edit Weight Record" : "Add Weight Record"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={editData.id ? handleEdit : handleAdd}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Weight*</label>
                  <div className="input-group">
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${
                        validationErrors.weight_value ? "is-invalid" : ""
                      }`}
                      placeholder={`${
                        addData.unit === "kg" ? "2-700" : "4-1550"
                      }`}
                      value={
                        editData.id
                          ? editData.weight_value
                          : addData.weight_value
                      }
                      name="weight_value"
                      onChange={handleOnChange}
                      onKeyPress={handleKeyPress}
                      min={addData.unit === "kg" ? "2" : "4"}
                      max={addData.unit === "kg" ? "700" : "1550"}
                      required
                    />
                    <select
                      className="form-select weight-modal-select"
                      style={{ maxWidth: "80px" }}
                      value={editData.id ? editData.unit : addData.unit}
                      onChange={editData.id ? undefined : handleUnitChange}
                      // disabled={!!editData.id}
                      name="unit"
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                    </select>
                  </div>
                  {validationErrors.weight_value && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.weight_value}
                    </div>
                  )}
                  <small className="form-text text-muted mt-0">
                    {addData.unit === "kg"
                      ? "Minimum: 2 kg, Maximum: 700 kg"
                      : "Minimum: 4 lbs, Maximum: 1550 lbs"}
                  </small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Date*</label>
                  <input
                    type="date"
                    className={`form-control weight-modal-select ${
                      validationErrors.record_date ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_date : addData.record_date
                    }
                    name="record_date"
                    onChange={handleOnChange}
                    max={today}
                    required
                  />
                  {validationErrors.record_date && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.record_date}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    placeholder="Add any notes about this measurement"
                    value={editData.id ? editData.notes : addData.notes}
                    name="notes"
                    onChange={handleOnChange}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              type="submit"
              disabled={LoadingEdit || isLoading}
            >
              {LoadingEdit || isLoading ? "Saving..." : "Save Record"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this weight record? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="btn btn-default"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={LoadingDelete}
          >
            {LoadingDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WeightManagementList;